<template>
  <section
    id="hero"
    class="hero-section"
    :class="{
      'hero-section--locked': locked || reduceMotion,
      'hero-section--reduced': reduceMotion,
    }"
    :data-state="heroState"
    @mouseenter="onPointerEnter"
    @mousemove="touchLock"
    @mouseleave="onPointerLeave"
    @focusin="onPointerEnter"
    @focusout="onPointerLeave"
    @wheel="touchLock"
    @touchmove="touchLock"
  >
    <div class="hero-section__wrap">
      <div class="hero-copy">
        <div
          class="hero-toggle"
          role="tablist"
          aria-label="Elige qué quieres ver"
          @keydown="onToggleKeydown"
        >
          <button
            v-for="domain in domains"
            :id="tabId(domain.id)"
            :key="domain.id"
            type="button"
            role="tab"
            :aria-selected="domain.id === activeId ? 'true' : 'false'"
            aria-controls="hero"
            @click="onToggle(domain.id)"
          >
            {{ toggleLabel(domain.trackState) }}
            <span class="hero-toggle__bar" aria-hidden="true">
              <i :style="{ width: barWidth(domain.id) }"></i>
            </span>
          </button>
        </div>

        <h1>
          <span class="hero-anchor">Tu historia merece ser</span>
          <span class="hero-word-slot" aria-live="polite">
            <span
              v-for="domain in domains"
              :key="domain.id"
              class="hero-word"
              :data-tone="domain.tone"
              :aria-hidden="domain.id === activeId ? 'false' : 'true'"
            >
              {{ domain.heroWord }}
            </span>
          </span>
        </h1>

        <div class="hero-sub">
          <p
            v-for="domain in domains"
            :key="domain.id"
            :data-state="domain.trackState"
          >
            {{ domain.heroDescription }}
          </p>
        </div>

        <div class="hero-actions">
          <v-btn class="hero-cta" size="x-large" rounded="lg" @click="onStart">
            Empezar gratis
          </v-btn>
          <div class="hero-secondary">
            <a
              v-for="domain in domains"
              :key="domain.id"
              :href="landingSectionHref(domain.anchor)"
              :data-state="domain.trackState"
              :aria-hidden="domain.id === activeId ? 'false' : 'true'"
              @click="onSecondary($event, domain.anchor)"
            >
              {{ domain.heroLinkLabel }}
            </a>
          </div>
        </div>

        <p class="hero-trust">
          Sin registro · Proyectos sin límite · Novela, cuentos, poesía o guion
          <span class="hero-trust__lock">
            <span aria-hidden="true">🔒</span>
            Tu obra es privada: ninguna IA la lee
          </span>
        </p>
        <p class="hero-tertiary">
          <span>
            ¿Vienes por la comunidad?
            <a href="/login?mode=register" @click="onProfile">Crea tu perfil de escritor →</a>
          </span>
        </p>
      </div>

      <div class="hero-visual">
        <figure class="hero-frame" data-state="escribir" :aria-hidden="heroState !== 'escribir'">
          <div class="hero-frame__bar" aria-hidden="true"><i></i><i></i><i></i></div>
          <img
            :src="shotEditor"
            alt="Editor de Escribonauta con el capítulo abierto"
            width="1400"
            height="937"
          />
        </figure>
        <figure class="hero-frame" data-state="compartir" :aria-hidden="heroState !== 'compartir'">
          <div class="hero-frame__bar" aria-hidden="true"><i></i><i></i><i></i></div>
          <img
            :src="shotComunidad"
            alt="Grupo de la comunidad con el foro y el chat"
            width="1400"
            height="937"
          />
        </figure>
      </div>
    </div>

    <a class="hero-cue" href="/#selector" @click="onCue">
      <span>Explorar</span>
      <span class="hero-cue__btn" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
      </span>
    </a>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import posthog from 'posthog-js'
import { LANDING_DOMAINS } from '@/constants/landingCatalog'
import { landingSectionHref, navigateToLandingSection } from '@/utils/landingHomeNavigation'
import { prefersReducedMotion } from '@/utils/landingScroll'
import shotEditor from '@/assets/landing/shot_editor.jpg'
import shotComunidad from '@/assets/landing/shot_comunidad.jpg'

const HERO_DURATION_MS = { escribir: 9000, compartir: 7000 } as const
const HERO_LOCK_MS = 15000
const HERO_TOGGLE_LABEL = { escribir: '✍️ Escribirla', compartir: '👥 Compartirla' } as const

type HeroTrackState = keyof typeof HERO_DURATION_MS

type HeroClock = {
  elapsed: number
  last: number | null
  lastInteraction: number
  raf: number
}

const clocks = new WeakMap<object, HeroClock>()

function clockOf(vm: object): HeroClock {
  let clock = clocks.get(vm)
  if (!clock) {
    clock = { elapsed: 0, last: null, lastInteraction: 0, raf: 0 }
    clocks.set(vm, clock)
  }
  return clock
}

export default defineComponent({
  name: 'HomeHeroSection',

  emits: {
    'start-writing': () => true,
    'create-profile': () => true,
  },

  setup() {
    return {
      domains: LANDING_DOMAINS,
      landingSectionHref,
      shotEditor,
      shotComunidad,
    }
  },

  data() {
    return {
      activeId: LANDING_DOMAINS[0]?.id ?? '',
      reduceMotion: false,
      paused: false,
      locked: false,
      barPercent: 0,
      motionQuery: null as MediaQueryList | null,
    }
  },

  computed: {
    activeDomain() {
      return this.domains.find((domain) => domain.id === this.activeId) ?? this.domains[0]
    },

    heroState(): HeroTrackState {
      return this.activeDomain?.trackState ?? 'escribir'
    },
  },

  mounted() {
    this.reduceMotion = prefersReducedMotion()
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.motionQuery.addEventListener('change', this.onMotionPreferenceChange)
    }
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    document.addEventListener('click', this.touchLock, true)
    document.addEventListener('keydown', this.touchLock, true)
    if (!this.reduceMotion) this.startClock()
  },

  beforeUnmount() {
    this.stopClock()
    this.motionQuery?.removeEventListener('change', this.onMotionPreferenceChange)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    document.removeEventListener('click', this.touchLock, true)
    document.removeEventListener('keydown', this.touchLock, true)
    clocks.delete(this)
  },

  methods: {
    tabId(id: string) {
      return `hero-tab-${id}`
    },

    toggleLabel(state: HeroTrackState) {
      return HERO_TOGGLE_LABEL[state]
    },

    trackStateOf(id: string): HeroTrackState {
      return this.domains.find((domain) => domain.id === id)?.trackState ?? 'escribir'
    },

    barWidth(id: string) {
      if (id !== this.activeId || this.locked || this.reduceMotion) return '0%'
      return `${this.barPercent}%`
    },

    capture(event: string, properties: Record<string, string>) {
      if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || !import.meta.env.VITE_POSTHOG_HOST) return
      posthog.capture(event, properties)
    },

    applyState(id: string, by?: 'auto' | 'click') {
      this.activeId = id
      const clock = clockOf(this)
      clock.elapsed = 0
      this.barPercent = 0
      if (!by) return
      this.capture('hero_state_shown', { state: this.trackStateOf(id), by })
    },

    lock() {
      this.locked = true
      clockOf(this).lastInteraction = performance.now()
    },

    unlock() {
      this.locked = false
      const clock = clockOf(this)
      clock.elapsed = 0
      this.barPercent = 0
      this.capture('hero_autoplay_resumed', { state: this.heroState })
    },

    touchLock() {
      if (!this.locked) return
      clockOf(this).lastInteraction = performance.now()
    },

    onPointerEnter() {
      this.paused = true
      this.touchLock()
    },

    onPointerLeave() {
      this.paused = false
      clockOf(this).last = null
      this.touchLock()
    },

    onFrame(now: number) {
      const clock = clockOf(this)
      if (clock.last == null) {
        clock.last = now
        return
      }
      const dt = now - clock.last
      clock.last = now
      if (this.locked) {
        if (now - clock.lastInteraction >= HERO_LOCK_MS) this.unlock()
        return
      }
      if (this.paused) return
      const duration = HERO_DURATION_MS[this.heroState]
      clock.elapsed += dt
      const next = Math.min(100, (clock.elapsed / duration) * 100)
      const rounded = Math.round(next * 10) / 10
      if (rounded !== this.barPercent) this.barPercent = rounded
      if (clock.elapsed >= duration) {
        const index = this.domains.findIndex((domain) => domain.id === this.activeId)
        const nextDomain = this.domains[(index + 1) % this.domains.length]
        if (nextDomain) this.applyState(nextDomain.id, 'auto')
      }
    },

    startClock() {
      const clock = clockOf(this)
      if (clock.raf || this.reduceMotion) return
      const loop = (now: number) => {
        clock.raf = requestAnimationFrame(loop)
        this.onFrame(now)
      }
      clock.raf = requestAnimationFrame(loop)
    },

    stopClock() {
      const clock = clockOf(this)
      if (!clock.raf) return
      cancelAnimationFrame(clock.raf)
      clock.raf = 0
      clock.last = null
    },

    onMotionPreferenceChange(event: MediaQueryListEvent) {
      this.reduceMotion = event.matches
      if (this.reduceMotion) {
        this.stopClock()
        this.barPercent = 0
        return
      }
      this.startClock()
    },

    onVisibilityChange() {
      if (document.hidden) {
        this.stopClock()
        return
      }
      this.startClock()
    },

    onToggle(id: string) {
      this.applyState(id, 'click')
      this.lock()
      this.capture('hero_toggle_click', { state: this.trackStateOf(id) })
    },

    onToggleKeydown(event: KeyboardEvent) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
      event.preventDefault()
      const index = this.domains.findIndex((domain) => domain.id === this.activeId)
      const next = this.domains[(index + 1) % this.domains.length]
      if (!next) return
      this.onToggle(next.id)
      this.$nextTick(() => {
        document.getElementById(this.tabId(next.id))?.focus()
      })
    },

    onStart() {
      this.capture('hero_cta', { hero_state: this.heroState })
      this.$emit('start-writing')
    },

    onProfile(event: MouseEvent) {
      event.preventDefault()
      this.capture('hero_profile', { hero_state: this.heroState })
      this.$emit('create-profile')
    },

    async onSecondary(event: MouseEvent, anchor: string) {
      event.preventDefault()
      await navigateToLandingSection(this.$router, anchor)
    },

    async onCue(event: MouseEvent) {
      event.preventDefault()
      this.capture('hero_scroll_cue', { hero_state: this.heroState })
      await navigateToLandingSection(this.$router, 'selector')
    },
  },
})
</script>

<style scoped>
.hero-section {
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  color: #1d3557;
  background: #fff3e4;
  transition: color 0.6s ease;
}

.hero-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(145deg, #1b8aa3, #145c78 52%, #0d3b66);
  opacity: 0;
  transition: opacity 0.6s ease;
  pointer-events: none;
}

.hero-section[data-state='compartir'] {
  color: #f4fbff;
}

.hero-section[data-state='compartir']::before {
  opacity: 1;
}

.hero-section__wrap,
.hero-cue {
  position: relative;
  z-index: 1;
}

@media (min-width: 1021px) {
  .hero-section {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - var(--landing-nav-height, 4rem));
    min-height: calc(100svh - var(--landing-nav-height, 4rem));
  }

  .hero-section__wrap {
    flex: 1;
    padding-top: 32px;
    padding-bottom: 112px;
  }
}

.hero-section__wrap {
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;
  width: min(1200px, 100%);
  margin: 0 auto;
  padding: 56px 24px 72px;
}

.hero-toggle {
  display: inline-flex;
  position: relative;
  gap: 4px;
  margin-bottom: 26px;
  padding: 5px;
  border-radius: 999px;
  background: rgba(29, 53, 87, 0.08);
}

.hero-section[data-state='compartir'] .hero-toggle {
  background: rgba(255, 255, 255, 0.14);
}

.hero-toggle button {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: 700 15px inherit;
  cursor: pointer;
  transition:
    background 0.3s,
    color 0.3s;
}

.hero-toggle button[aria-selected='true'] {
  background: #fff;
  color: #1d3557;
  box-shadow: 0 2px 10px rgba(29, 53, 87, 0.14);
}

.hero-toggle button:focus-visible {
  outline: 3px solid #ff9f1c;
  outline-offset: 2px;
}

.hero-toggle__bar {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 4px;
  height: 3px;
  border-radius: 3px;
  background: rgba(29, 53, 87, 0.12);
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.2s;
}

.hero-toggle button[aria-selected='true'] .hero-toggle__bar {
  opacity: 1;
}

.hero-toggle__bar i {
  display: block;
  height: 100%;
  width: 0;
  border-radius: 3px;
  background: #e06a00;
}

.hero-section[data-state='compartir'] .hero-toggle__bar i {
  background: #1b8aa3;
}

.hero-section--locked .hero-toggle__bar {
  opacity: 0 !important;
}

.hero-section h1 {
  margin: 0;
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -0.03em;
}

.hero-anchor {
  display: block;
  margin-bottom: 4px;
  font-size: clamp(30px, 3.6vw, 50px);
  white-space: nowrap;
}

.hero-word-slot {
  display: inline-grid;
  vertical-align: bottom;
  font-size: clamp(44px, 5.6vw, 76px);
}

.hero-word {
  grid-area: 1 / 1;
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
}

.hero-word[data-tone='warm'] {
  color: #e06a00;
}

.hero-word[data-tone='cool'] {
  color: #ffe08a;
}

.hero-section[data-state='escribir'] .hero-word[data-tone='cool'],
.hero-section[data-state='compartir'] .hero-word[data-tone='warm'] {
  opacity: 0;
  transform: translateY(12px);
}

.hero-sub {
  display: grid;
  max-width: 540px;
  min-height: calc(1.55em * 3);
  margin: 22px 0 30px;
  font-size: 19px;
  line-height: 1.55;
}

.hero-sub p {
  grid-area: 1 / 1;
  margin: 0;
  transition: opacity 0.4s ease;
}

.hero-section[data-state='escribir'] .hero-sub p[data-state='compartir'],
.hero-section[data-state='compartir'] .hero-sub p[data-state='escribir'] {
  opacity: 0;
  visibility: hidden;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 22px;
}

.hero-cta {
  height: 56px;
  padding: 0 30px;
  border-radius: 8px;
  background: #e06a00 !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 19px;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow:
    0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 2px 2px rgba(0, 0, 0, 0.14),
    0 1px 5px rgba(0, 0, 0, 0.12);
}

.hero-cta:hover {
  background: #c45a00 !important;
}

.hero-cta:focus-visible {
  outline: 3px solid #1d3557;
  outline-offset: 3px;
}

.hero-secondary {
  display: grid;
  font-weight: 700;
  font-size: 16px;
}

.hero-secondary a {
  grid-area: 1 / 1;
  justify-self: start;
  padding: 6px 2px;
  color: inherit;
  text-decoration: none;
  border-bottom: 2px solid currentColor;
  transition: opacity 0.3s;
}

.hero-section[data-state='escribir'] .hero-secondary a[data-state='compartir'],
.hero-section[data-state='compartir'] .hero-secondary a[data-state='escribir'] {
  opacity: 0;
  visibility: hidden;
}

.hero-trust {
  margin: 18px 0 0;
  font-size: 14.5px;
  line-height: 1.5;
  opacity: 0.9;
}

.hero-trust__lock {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-weight: 600;
}

.hero-tertiary {
  min-height: 26px;
  margin: 18px 0 0;
  font-size: 15.5px;
}

.hero-tertiary a {
  font-weight: 700;
  color: #ffe08a;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 224, 138, 0.6);
}

.hero-section[data-state='escribir'] .hero-tertiary > * {
  visibility: hidden;
}

.hero-visual {
  display: grid;
  min-width: 0;
}

.hero-frame {
  grid-area: 1 / 1;
  margin: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(29, 53, 87, 0.08);
  background: #fff;
  box-shadow: 0 22px 60px rgba(16, 36, 63, 0.22);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.hero-frame__bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  background: #eef1f5;
}

.hero-frame__bar i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d6d9db;
}

.hero-frame img {
  display: block;
  width: 100%;
  height: auto;
}

.hero-section[data-state='escribir'] .hero-frame[data-state='compartir'],
.hero-section[data-state='compartir'] .hero-frame[data-state='escribir'] {
  opacity: 0;
  transform: translateY(14px) scale(0.985);
  pointer-events: none;
}

.hero-cue {
  display: none;
}

@media (min-width: 1021px) {
  .hero-cue {
    position: absolute;
    left: 50%;
    bottom: 22px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: inherit;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    text-decoration: none;
    opacity: 0.85;
    transform: translateX(-50%);
  }

  .hero-cue:hover {
    opacity: 1;
  }

  .hero-cue__btn {
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    border: 2px solid currentColor;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.55);
    animation: hero-cue-bounce 2.2s ease-in-out infinite;
  }

  .hero-section[data-state='compartir'] .hero-cue__btn {
    background: rgba(255, 255, 255, 0.12);
  }

  .hero-cue__btn svg {
    width: 20px;
    height: 20px;
  }

  .hero-cue:focus-visible .hero-cue__btn {
    outline: 3px solid #ff9f1c;
    outline-offset: 3px;
  }
}

@keyframes hero-cue-bounce {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(6px);
  }
}

@media (max-width: 1020px) {
  .hero-section__wrap {
    grid-template-columns: 1fr;
    gap: 36px;
    padding-top: 40px;
    padding-bottom: 56px;
  }
}

@media (max-width: 640px) {
  .hero-section__wrap {
    gap: 30px;
    padding: 28px 18px 40px;
  }

  .hero-toggle {
    display: flex;
    width: 100%;
    margin-bottom: 22px;
  }

  .hero-toggle button {
    flex: 1;
    justify-content: center;
    padding: 10px 8px 12px;
    font-size: 14.5px;
  }

  .hero-anchor {
    font-size: 30px;
    white-space: normal;
  }

  .hero-word-slot {
    font-size: 50px;
  }

  .hero-sub {
    min-height: calc(1.55em * 4);
    margin: 16px 0 22px;
    font-size: 17px;
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .hero-cta {
    width: 100%;
  }

  .hero-secondary {
    justify-self: center;
    text-align: center;
  }

  .hero-secondary a {
    justify-self: center;
  }

  .hero-trust {
    text-align: center;
    font-size: 14px;
  }

  .hero-trust__lock {
    justify-content: center;
  }

  .hero-tertiary {
    min-height: 44px;
    text-align: center;
  }
}

.hero-section--reduced,
.hero-section--reduced::before,
.hero-section--reduced .hero-word,
.hero-section--reduced .hero-sub p,
.hero-section--reduced .hero-frame,
.hero-section--reduced .hero-secondary a,
.hero-section--reduced .hero-cue__btn {
  transition: none;
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .hero-section,
  .hero-section::before,
  .hero-word,
  .hero-sub p,
  .hero-frame,
  .hero-secondary a,
  .hero-cue__btn {
    transition: none;
    animation: none;
  }
}
</style>
